import React, { useMemo } from "react";
import { Avatar, Box, Chip, Divider, Stack, Typography } from "@mui/material";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import GlassCard from "./GlassCard";
import {
  buildEventDetailsMockFromState,
  minTicketPrice,
  getBannerSrc,
  formatBRL,
} from "../utils/normalizers";

function Metric({ icon, label, value }) {
  return (
    <Stack
      direction="row"
      spacing={1.2}
      alignItems="center"
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: "1px solid rgba(255,255,255,0.08)",
        bgcolor: "rgba(255,255,255,0.03)",
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 34,
          height: 34,
          bgcolor: "rgba(0,225,255,0.12)",
          color: "#8ef4ff",
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.46)", lineHeight: 1.1 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "rgba(255,255,255,0.92)" }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function RightPreview({ state }) {
  const mock = useMemo(() => buildEventDetailsMockFromState(state), [state]);
  const banner = getBannerSrc(state);
  const minPrice = minTicketPrice(state);
  const totalTickets = state.ingressos.reduce((acc, item) => acc + Number(item.quantidade || 0), 0);

  return (
    <Box sx={{ position: { xl: "sticky" }, top: { xl: 28 } }}>
      <Stack spacing={2}>
        <GlassCard sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              position: "relative",
              minHeight: { xs: 210, md: 250 },
              backgroundImage: banner
                ? `linear-gradient(180deg, rgba(5,8,16,0.08), rgba(5,8,16,0.68)), url(${banner})`
                : "linear-gradient(135deg, rgba(0,225,255,0.16), rgba(109,255,178,0.1))",
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ p: 2 }}
            >
              <Chip
                label="Preview em tempo real"
                sx={{
                  fontWeight: 900,
                  bgcolor: "rgba(5,12,24,0.58)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              />
              <Chip
                icon={<ConfirmationNumberRoundedIcon sx={{ fontSize: 16 }} />}
                label={`${state.ingressos.length} tipo(s) de ingresso`}
                sx={{
                  fontWeight: 800,
                  bgcolor: "rgba(5,12,24,0.58)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              />
            </Stack>
          </Box>

          <Box sx={{ p: 2.2 }}>
            <Stack spacing={1.4}>
              <Box>
                <Typography sx={{ fontSize: 12, letterSpacing: 1.4, textTransform: "uppercase", color: "rgba(255,255,255,0.45)", mb: 0.8 }}>
                  Página pública
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 950, letterSpacing: -0.6, mb: 0.8 }}>
                  {mock.title || "Título do evento"}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip size="small" label={mock.city || "Cidade · UF"} sx={{ fontWeight: 800 }} />
                  <Chip size="small" label={mock.date || "Data"} sx={{ fontWeight: 800 }} />
                  <Chip size="small" label={mock.time || "Horário"} sx={{ fontWeight: 800 }} />
                  <Chip size="small" label={`🔞 ${mock.classification || "Classificação"}`} sx={{ fontWeight: 800 }} />
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 1.6,
                  borderRadius: 2,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background:
                    "linear-gradient(135deg, rgba(0,225,255,0.1), rgba(109,255,178,0.08))",
                }}
              >
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.54)", mb: 0.4 }}>
                  Valor inicial exibido
                </Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 950, letterSpacing: -0.8 }}>
                  {formatBRL(minPrice)}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.62)" }}>
                  Usado como base para o destaque do ingresso na página do evento.
                </Typography>
              </Box>

              <Stack spacing={1}>
                <Metric icon={<EventRoundedIcon fontSize="small" />} label="Categoria" value={state.identidade.categoria || "Não definida"} />
                <Metric icon={<PlaceRoundedIcon fontSize="small" />} label="Local" value={state.dataLocal.local?.nome || "Nome do local"} />
                <Metric icon={<AccessTimeRoundedIcon fontSize="small" />} label="Portões" value={state.dataLocal.aberturaPortoes || "Não informado"} />
                <Metric icon={<LocalOfferRoundedIcon fontSize="small" />} label="Tags" value={state.identidade.tags?.length ? state.identidade.tags.join(", ") : "Nenhuma tag cadastrada"} />
              </Stack>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

              <Box>
                <Typography sx={{ fontWeight: 900, mb: 0.8 }}>Descrição</Typography>
                <Typography
                  sx={{
                    whiteSpace: "pre-line",
                    color: "rgba(255,255,255,0.72)",
                    lineHeight: 1.65,
                    fontSize: 14,
                  }}
                >
                  {mock.description?.trim()
                    ? mock.description
                    : "A descrição do evento vai aparecer aqui conforme você preencher os campos do formulário."}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </GlassCard>

        <GlassCard sx={{ p: 2 }}>
          <Stack spacing={1.2}>
            <Typography sx={{ fontWeight: 900 }}>Resumo rápido</Typography>
            <Stack direction="row" spacing={1}>
              <Metric icon={<ConfirmationNumberRoundedIcon fontSize="small" />} label="Ingressos" value={`${state.ingressos.length} tipo(s)`} />
              <Metric icon={<ReceiptLongRoundedIcon fontSize="small" />} label="Unidades" value={`${totalTickets} no total`} />
            </Stack>
          </Stack>
        </GlassCard>
      </Stack>
    </Box>
  );
}
